import React, { useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Button } from '../../../components/ui/Button';
import { FiX, FiDownload, FiChevronLeft } from 'react-icons/fi';

const FullPageOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${theme.colors.white};
  z-index: ${theme.zIndex.modal + 1};
  display: flex;
  flex-direction: column;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const PDFHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.light.border.light};
  background-color: ${theme.colors.white};
  box-shadow: ${theme.shadows.sm};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  flex: 1;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: ${theme.borderRadius.md};
  background-color: ${theme.colors.light.background.secondary};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
  }
`;

const DocumentTitle = styled.h2`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const PDFContainer = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PDFViewer = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background-color: ${theme.colors.light.background.secondary};
`;

interface PDFViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl: string;
  documentName: string;
  documentSize?: number;
  uploadedAt?: Date | string;
}

const PDFViewerModal: React.FC<PDFViewerModalProps> = ({
  isOpen,
  onClose,
  pdfUrl,
  documentName,
  documentSize,
  uploadedAt,
}) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = documentName || 'document.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!isOpen) return null;

  return (
    <FullPageOverlay>
      <PDFHeader>
        <HeaderLeft>
          <BackButton onClick={onClose} title="Back">
            <FiChevronLeft size={20} />
          </BackButton>
          <DocumentTitle>{documentName}</DocumentTitle>
        </HeaderLeft>
        <HeaderRight>
          <Button variant="secondary" onClick={handleDownload} size="sm">
            <FiDownload size={18} style={{ marginRight: theme.spacing[2] }} />
            Download
          </Button>
        </HeaderRight>
      </PDFHeader>
      <PDFContainer>
        <PDFViewer src={pdfUrl} title={documentName} />
      </PDFContainer>
    </FullPageOverlay>
  );
};

export default PDFViewerModal;

